import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Shield, ShieldOff } from "lucide-react";

interface TwoFactorSetupProps {
  enabled: boolean;
}

interface SetupResponse {
  qrCodeUrl: string;
  secret: string;
  backupCodes: string[];
}

export function TwoFactorSetup({ enabled }: TwoFactorSetupProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"idle" | "qr" | "backup" | "disable">(
    "idle",
  );
  const [setupData, setSetupData] = useState<SetupResponse | null>(null);
  const [token, setToken] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const setupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/2fa/setup");
      return (await res.json()) as SetupResponse;
    },
    onSuccess: (data) => {
      setSetupData(data);
      setStep("qr");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to setup 2FA",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const enableMutation = useMutation({
    mutationFn: async (verifyToken: string) => {
      const res = await apiRequest("POST", "/api/auth/2fa/enable", {
        token: verifyToken,
      });
      return (await res.json()) as { message: string; backupCodes?: string[] };
    },
    onSuccess: (data) => {
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
      } else if (setupData?.backupCodes) {
        setBackupCodes(setupData.backupCodes);
      }
      setStep("backup");
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "2FA enabled successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Invalid verification code",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const disableMutation = useMutation({
    mutationFn: async (verifyToken: string) => {
      await apiRequest("POST", "/api/auth/2fa/disable", {
        token: verifyToken,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "2FA disabled" });
      handleClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to disable 2FA",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function handleClose() {
    setOpen(false);
    setStep("idle");
    setSetupData(null);
    setToken("");
    setBackupCodes([]);
  }

  function handleOpenSetup() {
    setOpen(true);
    if (enabled) {
      setStep("disable");
    } else {
      setupMutation.mutate();
    }
  }

  return (
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Two-Factor Authentication</span>
          <Badge variant={enabled ? "default" : "secondary"}>
            {enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {enabled
            ? "Your account is protected with an authenticator app."
            : "Add an extra layer of security to your account."}
        </p>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? handleOpenSetup() : handleClose())}>
        <DialogTrigger asChild>
          <Button variant={enabled ? "outline" : "default"} size="sm">
            {enabled ? (
              <>
                <ShieldOff className="mr-2 h-4 w-4" />
                Disable
              </>
            ) : (
              <>
                <Shield className="mr-2 h-4 w-4" />
                Enable
              </>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent>
          {step === "qr" && setupData && (
            <>
              <DialogHeader>
                <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                <DialogDescription>
                  Scan the QR code with your authenticator app, then enter the
                  verification code.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-center py-4">
                <img
                  src={setupData.qrCodeUrl}
                  alt="2FA QR Code"
                  className="h-48 w-48"
                />
              </div>
              <div className="text-center text-sm text-muted-foreground">
                <p>Or enter this code manually:</p>
                <code className="mt-1 block font-mono text-xs tracking-widest">
                  {setupData.secret}
                </code>
              </div>
              <div className="space-y-2 pt-2">
                <Label htmlFor="verify-token">Verification Code</Label>
                <Input
                  id="verify-token"
                  placeholder="000000"
                  maxLength={6}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && token.length >= 6) {
                      enableMutation.mutate(token);
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => enableMutation.mutate(token)}
                  disabled={token.length < 6 || enableMutation.isPending}
                >
                  {enableMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Verify & Enable
                </Button>
              </div>
            </>
          )}

          {step === "backup" && backupCodes.length > 0 && (
            <>
              <DialogHeader>
                <DialogTitle>Save Your Backup Codes</DialogTitle>
                <DialogDescription>
                  Store these codes in a safe place. You can use them to access
                  your account if you lose your authenticator device.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-md border bg-muted/50 p-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((code, i) => (
                    <span key={i}>{code}</span>
                  ))}
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={handleClose}>Done</Button>
              </div>
            </>
          )}

          {step === "backup" && backupCodes.length === 0 && (
            <>
              <DialogHeader>
                <DialogTitle>2FA Enabled</DialogTitle>
                <DialogDescription>
                  Two-factor authentication has been enabled on your account.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end pt-2">
                <Button onClick={handleClose}>Done</Button>
              </div>
            </>
          )}

          {step === "disable" && (
            <>
              <DialogHeader>
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
                <DialogDescription>
                  Enter a verification code from your authenticator app to
                  confirm.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 pt-2">
                <Label htmlFor="disable-token">Verification Code</Label>
                <Input
                  id="disable-token"
                  placeholder="000000"
                  maxLength={6}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && token.length >= 6) {
                      disableMutation.mutate(token);
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => disableMutation.mutate(token)}
                  disabled={token.length < 6 || disableMutation.isPending}
                >
                  {disableMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Disable 2FA
                </Button>
              </div>
            </>
          )}

          {step === "idle" && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
